/**
 * Mapa da Produção — onde cada ambiente está, derivado da planilha.
 *
 * Regra do dono (plano §7.6): DATA PREENCHIDA = ETAPA FINALIZADA. O ambiente
 * está na etapa que vem DEPOIS da etapa mais avançada com data; datas
 * anteriores vazias contam como concluídas. Nada aqui grava: é leitura pura da
 * linha da planilha, para o tablet não contradizer a planilha.
 *
 * MUDANÇA DA V3 — SETOR DEIXOU DE SER CÓDIGO FIXO. Até a v2 existiam cinco
 * setores escritos no código (corte, bordo, furacao, marcenaria, expedicao).
 * Agora o gestor CADASTRA os setores (desenha no mapa, dá o nome que a fábrica
 * usa) e amarra cada um a uma ETAPA da planilha (`dados/etapasPlanilha.js`).
 * Então este módulo trabalha com duas coisas diferentes:
 *
 *   ETAPA  — o que a planilha diz (código fixo, 29 etapas, catálogo lido da
 *            aba GERENCIAL). É o que faz o ambiente andar.
 *   SETOR  — a sala que o gestor desenhou (id do backend, nome editável).
 *            Vários setores podem apontar para a MESMA etapa; um deles RECEBE
 *            os ambientes que chegam pela planilha e o gestor move para os
 *            outros quando quiser (`dados/combinar.js`).
 *
 * Etapa sem nenhum setor cadastrado não perde o ambiente: ele vai para a
 * bandeja com o rótulo da etapa — "Ainda no escritório" (etapas de escritório)
 * ou "Sem sala no mapa" (etapa de fábrica sem sala nenhuma no fluxo). Etapa de
 * fábrica sem sala PRÓPRIA vai para a sala vizinha do fluxo (`salaVizinha`),
 * com o selo "aguardando". É o que substitui a
 * bandeja "Ainda no escritório" da v2 — agora ela também recebe etapas de
 * fábrica que o gestor ainda não desenhou (usinagem, separação…).
 */
import { hasValue, isNo, isYes, normalizeText } from "../../utils/data";
import { plural } from "../../utils/dinheiro";
import { parseMarceneiro } from "../../utils/entregas";
import { F } from "../../utils/gerencialFields";
import { compareDatesAsc, isBeforeToday, startOfDay, toDate } from "../../utils/status";
import {
  ETAPAS_PLANILHA,
  ETAPAS_SEQUENCIAIS,
  ETAPA_POR_CODIGO,
  dataDaEtapa,
  etapaAtualDoAmbiente,
  etapasParalelasPendentes,
  macroDaEtapa,
  rotuloDoVinculo,
  vinculoExiste,
} from "./dados/etapasPlanilha";

export const SITUACAO = Object.freeze({
  /** Existe setor cadastrado recebendo a etapa atual: o ambiente está na sala. */
  SETOR: "setor",
  /** Nenhuma sala recebe o ambiente: bandeja "Ainda no escritório" / "Sem sala no mapa". */
  SEM_SETOR: "sem_setor",
  /** Obra ainda não liberada (F = SIM): fora do fluxo, bandeja própria. */
  OBRA_FUTURA: "obra_futura",
  /**
   * Guarda de migração da v2 → v3. `SITUACAO.ESCRITORIO` sumiu quando os
   * setores viraram cadastro: comparar contra `undefined` daria uma bandeja
   * vazia sem erro nenhum, que é o pior desfecho possível. Remover quando
   * nenhuma tela do Mapa mencionar mais o nome.
   */
  get ESCRITORIO() {
    throw new Error(
      'SITUACAO.ESCRITORIO saiu na v3 do Mapa da Produção: use SITUACAO.SEM_SETOR (bandeja "Sem setor no mapa").'
    );
  },
});

const MS_POR_DIA = 24 * 60 * 60 * 1000;
// Macro-grupo das etapas de escritório (medição, executivo, aprovação…).
const MACRO_ESCRITORIO = "info_pedido";
const SEM_TEXTO = "";
const LISTA_VAZIA = Object.freeze([]);

/** Tipos de aviso que o catálogo levanta para a tela mostrar ao gestor. */
export const AVISO_CATALOGO = Object.freeze({
  SEM_RECEPTOR: "sem_receptor",
  VARIOS_RECEPTORES: "varios_receptores",
  ETAPA_DESCONHECIDA: "etapa_desconhecida",
});

// Prazo do ambiente quando a etapa não tem coluna LIMITE (ou ela está vazia):
// vale a promessa feita ao cliente, da mais recente para a mais antiga —
// reprogramada (CR), entrega calculada pela otimização (CO) e, sem nenhuma
// delas, a entrega prevista na venda (N), que é a única que os 75 ambientes
// ainda não otimizados têm.
const LIMITES_DE_ENTREGA = [F.DATA_REPROGRAMADA, F.ENTREGA_CLIENTE, F.ENTREGA_PREVISTA];

// "Desde quando está aqui": a data de marco mais recente da linha. A venda
// entra para o ambiente que ainda não tem nenhum marco ter uma data.
const CAMPOS_DE_MARCO = Object.freeze([
  F.DATA_DA_VENDA,
  ...ETAPAS_SEQUENCIAIS.map((etapa) => etapa.campo),
]);

const porOrdemENome = (a, b) =>
  (a.ordem ?? 0) - (b.ordem ?? 0) ||
  String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR", { numeric: true });

/**
 * Rótulo do vínculo com a planilha: a etapa ("Corte", "Compras — MDF e
 * bordo") ou o grupo inteiro ("todo o grupo Expedição e entrega").
 */
export function rotuloDaEtapa(codigo) {
  return rotuloDoVinculo(codigo) || SEM_TEXTO;
}

// ---------------------------------------------------------------- catálogo

function setoresOrdenados(brutos) {
  return (Array.isArray(brutos) ? brutos : [])
    .filter((setor) => setor && setor.id)
    .map((setor) => Object.freeze({ ...setor, ativo: setor.ativo !== false }))
    .sort(porOrdemENome);
}

// Sem ninguém marcado, o primeiro da ordem recebe: é melhor o ambiente cair
// numa sala da etapa certa do que sumir na bandeja por causa de uma flag.
// O `aviso` é que conta essa história para o gestor.
function receptorDaEtapa(daEtapa) {
  return daEtapa.find((setor) => setor.recebe_da_planilha) || daEtapa[0] || null;
}

function avisoDeReceptor(marcados) {
  if (marcados.length === 0) return AVISO_CATALOGO.SEM_RECEPTOR;
  return marcados.length > 1 ? AVISO_CATALOGO.VARIOS_RECEPTORES : null;
}

function avisosDoCatalogo(porEtapa, todos) {
  const avisos = todos
    .filter((setor) => setor.ativo && !vinculoExiste(setor.etapa))
    .map((setor) => ({
      tipo: AVISO_CATALOGO.ETAPA_DESCONHECIDA,
      etapa: setor.etapa || null,
      rotulo: setor.nome,
      setores: [setor],
    }));
  porEtapa.forEach((daEtapa, etapa) => {
    const tipo = avisoDeReceptor(daEtapa.filter((setor) => setor.recebe_da_planilha));
    if (tipo) avisos.push({ tipo, etapa, rotulo: rotuloDaEtapa(etapa), setores: daEtapa });
  });
  return avisos;
}

/**
 * Catálogo dos setores cadastrados — o índice que todo o resto do módulo usa.
 * `brutos` = a lista do backend (`/estado.setores`), cada item:
 *
 *   {id, nome, etapa, recebe_da_planilha, ordem, ativo, forma, versao}
 *
 * Devolve `{setores, todos, ids, porId, porEtapa, vazio, avisos, setor(),
 * nome(), ordem(), etapaDoSetor(), setoresDaEtapa(), setorQueRecebe()}`.
 *
 * `setores` só tem os ATIVOS (é o que o mapa desenha); `todos` inclui os
 * desativados, para a tela ainda conseguir escrever o nome de um setor antigo
 * que aparece num evento da linha do tempo.
 *
 * Função pura e barata: a tela cria um por leitura de `/estado` (useMemo).
 */
export function criarCatalogoSetores(brutos) {
  const todos = setoresOrdenados(brutos);
  const setores = todos.filter((setor) => setor.ativo);
  const porId = new Map(todos.map((setor) => [setor.id, setor]));
  // A chave é o VÍNCULO: o código da etapa ou o do macro-grupo. Duas salas
  // ligadas ao mesmo grupo disputam a entrada entre si; uma ligada ao grupo e
  // outra a uma etapa dele NÃO disputam — a da etapa é mais específica e
  // ganha o ambiente daquela etapa (`setorQueRecebe`).
  const porEtapa = new Map();
  setores.forEach((setor) => {
    if (!vinculoExiste(setor.etapa)) return;
    if (!porEtapa.has(setor.etapa)) porEtapa.set(setor.etapa, []);
    porEtapa.get(setor.etapa).push(setor);
  });
  const receptores = new Map([...porEtapa].map(([etapa, lista]) => [etapa, receptorDaEtapa(lista)]));
  const receptorDoVinculo = (codigo) =>
    receptores.get(codigo) || receptores.get(macroDaEtapa(codigo)) || null;
  const cobreEtapa = (setorId, codigo) => {
    const vinculo = porId.get(setorId)?.etapa;
    return Boolean(vinculo) && (vinculo === codigo || vinculo === macroDaEtapa(codigo));
  };

  return Object.freeze({
    setores,
    todos,
    ids: setores.map((setor) => setor.id),
    porId,
    porEtapa,
    vazio: setores.length === 0,
    avisos: avisosDoCatalogo(porEtapa, todos),
    setor: (setorId) => porId.get(setorId) || null,
    nome: (setorId) => porId.get(setorId)?.nome || SEM_TEXTO,
    ordem: (setorId) => porId.get(setorId)?.ordem ?? Number.MAX_SAFE_INTEGER,
    // Etapa do setor: só quando o vínculo é uma etapa. Sala ligada ao grupo
    // inteiro não tem UMA etapa — quem pergunta "esta sala cobre a etapa X?"
    // usa `cobreEtapa`.
    etapaDoSetor: (setorId) => ETAPA_POR_CODIGO[porId.get(setorId)?.etapa] || null,
    cobreEtapa,
    setoresDaEtapa: (codigo) => porEtapa.get(codigo) || LISTA_VAZIA,
    setorQueRecebe: receptorDoVinculo,
  });
}

/** Catálogo de quem ainda não desenhou nada (tudo cai na bandeja). */
export const CATALOGO_VAZIO = criarCatalogoSetores([]);

// ------------------------------------------------------ leitura da linha

function primeiraData(row, campos) {
  for (const campo of campos) {
    const data = toDate(row[campo]);
    if (data) return data;
  }
  return null;
}

function ultimoMarco(row) {
  return CAMPOS_DE_MARCO.reduce((maior, campo) => {
    const data = dataDaEtapa(row, { campo });
    return data && (!maior || data > maior) ? data : maior;
  }, null);
}

// Reprogramado primeiro: é a promessa mais recente, como na v2 (marcenaria).
function prazoDaEtapa(row, etapa) {
  const limites = [etapa?.campoLimiteReprogramado, etapa?.campoLimite].filter(Boolean);
  return primeiraData(row, limites) || primeiraData(row, LIMITES_DE_ENTREGA);
}

/**
 * Etapa em execução: só a marcenaria tem coluna de INÍCIO na planilha (BV).
 * Nas outras, "em produção" é o gestor quem aponta no app (`combinar.js`).
 */
function etapaIniciada(row, etapa) {
  return Boolean(etapa?.campoInicio) && Boolean(dataDaEtapa(row, { campo: etapa.campoInicio }));
}

/**
 * Pendências paralelas (terceiros, acabamento, insumos) que se aplicam a este
 * ambiente. Cada item: {codigo, grupo, texto}. O `codigo` é o da etapa
 * paralela — é por ele que o filtro de pendência casa.
 */
function pendenciasDaLinha(row) {
  return etapasParalelasPendentes(row).map((etapa) =>
    Object.freeze({ codigo: etapa.codigo, grupo: etapa.grupo || etapa.macro, texto: etapa.rotulo })
  );
}

function textoOuVazio(valor) {
  return hasValue(valor) ? String(valor).trim() : SEM_TEXTO;
}

function diasDesde(data, hoje) {
  if (!data) return null;
  return Math.max(0, Math.round((startOfDay(hoje) - startOfDay(data)) / MS_POR_DIA));
}

const MARCA_SERVICO = /^SERV/i;

/** Linha de serviço/assistência (lote "SERV."), não um ambiente de móvel novo. */
export function ehServico(lote) {
  return MARCA_SERVICO.test(textoOuVazio(lote));
}

/** Etapas do macro-grupo INFO PEDIDO: o ambiente ainda está no escritório. */
export function ehEtapaDoEscritorio(etapa) {
  return etapa?.macro === MACRO_ESCRITORIO;
}

/**
 * Sala que recebe um ambiente cuja etapa de FÁBRICA não tem sala desenhada: a
 * da próxima etapa do fluxo que tem sala (compras de MDF → a sala do corte) e,
 * sem nenhuma adiante, a da etapa anterior mais próxima (carregamento → a sala
 * da limpeza/embalagem). Foi a regra aprovada no plano (§7.6: "Corte — selo
 * material não confirmado") e é o que a v2 fazia. Etapa do escritório nunca
 * vai para uma sala: fica na bandeja "Ainda no escritório".
 */
function salaVizinha(etapa, catalogo) {
  if (ehEtapaDoEscritorio(etapa)) return null;
  const indice = ETAPAS_SEQUENCIAIS.findIndex((e) => e.codigo === etapa.codigo);
  if (indice < 0) return null;
  const daFabrica = (e) => !ehEtapaDoEscritorio(e);
  for (let i = indice + 1; i < ETAPAS_SEQUENCIAIS.length; i += 1) {
    const sala = catalogo.setorQueRecebe(ETAPAS_SEQUENCIAIS[i].codigo);
    if (sala) return sala;
  }
  for (let i = indice - 1; i >= 0 && daFabrica(ETAPAS_SEQUENCIAIS[i]); i -= 1) {
    const sala = catalogo.setorQueRecebe(ETAPAS_SEQUENCIAIS[i].codigo);
    if (sala) return sala;
  }
  return null;
}

/**
 * Deriva onde o ambiente está. `opcoes` = {catalogo, hoje}.
 *
 * Devolve `null` para o que não entra no Mapa: já carregado (CN preenchido) ou
 * sem OBRA FUTURA definida. Sem catálogo, tudo cai na bandeja "Sem setor no
 * mapa" — o mapa recém-instalado mostra a fábrica inteira numa lista, em vez
 * de esconder os ambientes.
 */
export function derivarSituacao(row, { catalogo = CATALOGO_VAZIO, hoje = new Date() } = {}) {
  if (!row || hasValue(row[F.CARREGAMENTO])) return null;
  const obraFutura = isYes(row[F.OBRA_FUTURA]);
  if (!obraFutura && !isNo(row[F.OBRA_FUTURA])) return null;

  const etapa = etapaAtualDoAmbiente(row);
  const direto = obraFutura || !etapa ? null : catalogo.setorQueRecebe(etapa.codigo);
  // Etapa de fábrica sem sala própria vai para a sala vizinha, com selo: um
  // ambiente esperando MDF está na porta do Corte, não "no escritório".
  const vizinha = direto || obraFutura || !etapa ? null : salaVizinha(etapa, catalogo);
  const setor = direto || vizinha;
  const situacao = obraFutura ? SITUACAO.OBRA_FUTURA : setor ? SITUACAO.SETOR : SITUACAO.SEM_SETOR;
  const prazo = prazoDaEtapa(row, etapa);
  const desde = ultimoMarco(row);
  const marceneiroBruto = row[F.MARCENEIRO_RESPONSAVEL];

  return {
    situacao,
    setorId: setor?.id || null,
    setorNome: setor?.nome || SEM_TEXTO,
    etapa: etapa?.codigo || null,
    etapaRotulo: etapa?.rotulo || SEM_TEXTO,
    // Está na sala vizinha à espera desta etapa (a sala dela não existe).
    aguardandoEtapa: vizinha ? etapa.rotulo : SEM_TEXTO,
    doEscritorio: ehEtapaDoEscritorio(etapa),
    emProducao: etapaIniciada(row, etapa),
    atrasada: Boolean(prazo) && isBeforeToday(prazo, hoje),
    prazo,
    desde,
    diasNoSetor: diasDesde(desde, hoje),
    pedido: textoOuVazio(row[F.NUMERO_DO_PEDIDO]),
    ambiente: textoOuVazio(row[F.AMBIENTE]),
    cliente: textoOuVazio(row[F.NOME_DO_CLIENTE]),
    lote: textoOuVazio(row[F.NUMERO_DO_LOTE]),
    // O PPCP marca assistência/serviço com "SERV." no lote: não é um móvel
    // novo, e a tela diz isso em vez de tratá-lo como ambiente de produção.
    servico: ehServico(row[F.NUMERO_DO_LOTE]),
    marceneiro: hasValue(marceneiroBruto) ? parseMarceneiro(marceneiroBruto) : null,
    pendencias: pendenciasDaLinha(row),
    row,
  };
}

/** Deriva todas as linhas, descartando o que não entra no Mapa. */
export function derivarDemandas(linhas, opcoes = {}) {
  if (!Array.isArray(linhas)) return [];
  return linhas
    .map((row, indice) => {
      const demanda = derivarSituacao(row, opcoes);
      return demanda ? { ...demanda, chave: `${demanda.pedido}|${demanda.ambiente}|${indice}` } : null;
    })
    .filter(Boolean);
}

// -------------------------------------------------------------- resumos

const contagemVazia = () => ({ total: 0, atrasadas: 0, emProducao: 0 });

/**
 * Contagens por setor cadastrado + bandejas:
 * {porSetor: {[setorId]: {total, atrasadas, emProducao}}, semSetor,
 * semSetorAtrasadas, soEscritorio, obraFutura}.
 * Com `catalogo`, todo setor cadastrado aparece (mesmo zerado) — é o que faz a
 * sala vazia continuar desenhada no mapa.
 */
export function resumirMapa(demandas, catalogo = CATALOGO_VAZIO) {
  const porSetor = Object.fromEntries(catalogo.ids.map((id) => [id, contagemVazia()]));
  let semSetor = 0;
  let semSetorAtrasadas = 0;
  let semSetorForaDoEscritorio = 0;
  let obraFutura = 0;
  (demandas || []).forEach((demanda) => {
    if (demanda.situacao === SITUACAO.OBRA_FUTURA) {
      obraFutura += 1;
      return;
    }
    if (demanda.situacao === SITUACAO.SEM_SETOR || !demanda.setorId) {
      semSetor += 1;
      if (demanda.atrasada) semSetorAtrasadas += 1;
      if (!demanda.doEscritorio) semSetorForaDoEscritorio += 1;
      return;
    }
    // Setor que não está no catálogo (desativado depois de o gestor mover um
    // ambiente para lá) ainda conta: some-lo esconderia trabalho em andamento.
    if (!porSetor[demanda.setorId]) porSetor[demanda.setorId] = contagemVazia();
    const contagem = porSetor[demanda.setorId];
    contagem.total += 1;
    if (demanda.atrasada) contagem.atrasadas += 1;
    if (demanda.emProducao) contagem.emProducao += 1;
  });
  // O atraso da bandeja fica à vista: fora das salas ele não entra na faixa
  // do topo (que é da fábrica) e sumia de todo indicador.
  return { porSetor, semSetor, semSetorAtrasadas, soEscritorio: semSetorForaDoEscritorio === 0, obraFutura };
}

/**
 * Linha de atenção do setor: até dois contadores com texto e o tom.
 * Cor só comunica estado — `danger` quando há atrasada, neutro senão.
 */
export function atencaoDoSetor(contagem) {
  const partes = [];
  if (contagem?.atrasadas) partes.push(plural(contagem.atrasadas, "atrasada", "atrasadas"));
  if (contagem?.emProducao) partes.push(`${contagem.emProducao} em produção`);
  return {
    texto: partes.length ? partes.join(" · ") : "Sem atrasos",
    tom: contagem?.atrasadas ? "danger" : "neutral",
  };
}

function porPrazo(a, b) {
  return compareDatesAsc(a.prazo, b.prazo) || a.cliente.localeCompare(b.cliente, "pt-BR");
}

/** Blocos da lista do setor: Atrasadas → Em produção → Aguardando, por prazo. */
export function blocosDoSetor(demandas, setorId) {
  const doSetor = (demandas || []).filter((d) => d.situacao === SITUACAO.SETOR && d.setorId === setorId);
  const blocos = [
    { chave: "atrasadas", titulo: "Atrasadas", tom: "danger", itens: doSetor.filter((d) => d.atrasada) },
    {
      chave: "em_producao",
      titulo: "Em produção",
      tom: "info",
      itens: doSetor.filter((d) => !d.atrasada && d.emProducao),
    },
    {
      chave: "aguardando",
      titulo: "Aguardando",
      tom: "neutral",
      itens: doSetor.filter((d) => !d.atrasada && !d.emProducao),
    },
  ];
  return blocos
    .map((b) => ({ ...b, itens: [...b.itens].sort(porPrazo) }))
    .filter((b) => b.itens.length > 0);
}

/**
 * Bandeja ("Ainda no escritório"/"Sem sala no mapa" ou "Obra futura") agrupada pela ETAPA da
 * planilha, na ordem do fluxo. Substitui `gruposDoEscritorio` da v2: lá os
 * grupos eram as cinco etapas de escritório escritas no código, aqui são as
 * etapas de verdade, incluindo as de fábrica que ninguém desenhou ainda.
 */
export function gruposDaBandeja(demandas, situacao = SITUACAO.SEM_SETOR) {
  const daBandeja = (demandas || []).filter((d) => d.situacao === situacao);
  return ETAPAS_PLANILHA.map((etapa) => ({
    chave: etapa.codigo,
    titulo: etapa.rotulo,
    tom: "neutral",
    itens: daBandeja.filter((d) => d.etapa === etapa.codigo).sort(porPrazo),
  })).filter((grupo) => grupo.itens.length > 0);
}

/** Busca por cliente, pedido, ambiente, lote ou marceneiro (sem acento/caixa). */
export function buscarDemandas(demandas, termo) {
  const alvo = normalizeText(termo);
  if (!alvo) return [];
  return (demandas || []).filter((d) =>
    [d.cliente, d.pedido, d.ambiente, d.lote, d.marceneiro?.nome].some((campo) =>
      normalizeText(campo).includes(alvo)
    )
  );
}

/**
 * Onde a demanda está, em texto: o nome do setor, ou a bandeja com o rótulo da
 * etapa ("Ainda no escritório · Medição", "Obra futura · Projeto executivo").
 */
export function localDaDemanda(demanda) {
  if (demanda?.situacao === SITUACAO.SETOR) return demanda.setorNome;
  const prefixo = demanda?.situacao === SITUACAO.OBRA_FUTURA
    ? "Obra futura"
    : demanda?.doEscritorio ? "Ainda no escritório" : "Sem sala no mapa";
  return demanda?.etapaRotulo ? `${prefixo} · ${demanda.etapaRotulo}` : prefixo;
}
